// react core
import { Fragment, useContext, useState } from "react";

// material design
import Box from "@mui/material/Box";
import Grow from "@mui/material/Grow";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

// fonts icons and colors
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose as iconClose } from "@fortawesome/pro-thin-svg-icons";

// entzy context and services
import configEntzy from "components/config/ConfigEntzy";
import { EventContext } from "components/event/EventContext";
import { ActionButton } from "components/utils/common/CommonButtons";
import { TextTag } from "components/utils/common/CommonTags";
import { createGeoCountryItemByCode } from "models/geo";

// entzy actions
import ActionCoreInfo from "./ActionCoreInfo";
import ActionLaunchpadRunner from "./ActionLaunchpadRunner";
import ActionSharingRider from "./ActionSharingRider";
import ActionSharingRunner from "./ActionSharingRunner";
import ActionDatesRider from "./ActionDatesRider";
import ActionDatesRunner from "./ActionDatesRunner";
import ActionMessagingRider from "./ActionMessagingRider";
import ActionMessagingRunner from "./ActionMessagingRunner";
import ActionLocationsRider from "./ActionLocationsRider";
import ActionLocationsRunner from "./ActionLocationsRunner";
import ActionEntryRider from "./ActionEntryRider";
import ActionEntryRunner from "./ActionEntryRunner";
import ActionMembersRider from "./ActionMembersRider";
import ActionMembersRunner from "./ActionMembersRunner";

function ActionCore(props) {
  const user = props.user;
  const eventContext = useContext(EventContext);
  const [runnerAction, setRunnerAction] = useState(
    props.runnerAction ? true : false,
  );
  const [riderAction, setRiderAction] = useState(
    props.riderAction ? true : false,
  );
  const [viewerType] = useState(
    eventContext.state.event.manager ? "runner" : "rider",
  );
  const [runnerViewer] = useState(eventContext.state.event.manager);
  const [riderViewer] = useState(!eventContext.state.event.manager);
  const [showInfo, setShowInfo] = useState(false);
  const onChangeCallback = props.onChange;

  const handleCloseInfo = async () => {
    setShowInfo(false);
  };
  const handleToggleAction = async ({
    runnerSide,
    riderSide,
    overrideAction,
  }) => {
    if (!user.connected && props.room !== "launchpad") {
      props.drawerConnectToggle();
    } else {
      let newRunnerAction = runnerAction;
      let newRiderAction = riderAction;
      if (overrideAction === true) {
        newRunnerAction = runnerSide && runnerViewer ? true : false;
        newRiderAction = riderSide && riderViewer ? true : false;
      } else {
        newRunnerAction = runnerSide && runnerViewer ? !runnerAction : false;
        newRiderAction = riderSide && riderViewer ? !riderAction : false;
      }
      setRunnerAction(newRunnerAction);
      setRiderAction(newRiderAction);
      if (onChangeCallback)
        onChangeCallback({
          runnerAction: newRunnerAction,
          riderAction: newRiderAction,
        });
    }
  };
  const handleOpenAction = async () => {
    return await handleToggleAction({
      runnerSide: runnerViewer,
      riderSide: riderViewer,
      overrideAction: true,
    });
  };

  const handleCloseAction = async () => {
    setRunnerAction(false);
    setRiderAction(false);
    if (onChangeCallback)
      onChangeCallback({
        runnerAction: false,
        riderAction: false,
      });
  };

  const renderActionButton = ({ runnerSide, riderSide }) => {
    // set button text state with counters
    let text = "";
    let side = runnerSide ? "runner" : "rider";
    let icon = props.icon;
    if ((runnerSide && runnerAction) || (riderSide && riderAction)) {
      text += "Close";
    } else {
      text +=
        eventContext.state.event.modules[
          eventContext.state.event.moduleSelected
        ].text[side].action;
      if (
        runnerViewer &&
        runnerSide &&
        eventContext.state.event.modules[
          eventContext.state.event.moduleSelected
        ].counters.runner.viewer.actionInfo
      ) {
        text +=
          eventContext.state.event.modules[
            eventContext.state.event.moduleSelected
          ].counters.runner.viewer.actionInfo;
      }
      if (
        riderViewer &&
        riderSide &&
        eventContext.state.event.modules[
          eventContext.state.event.moduleSelected
        ].counters.rider.viewer.actionInfo
      ) {
        text +=
          eventContext.state.event.modules[
            eventContext.state.event.moduleSelected
          ].counters.rider.viewer.actionInfo;
      }
    }
    // overrides if provided
    if (props.actionText) {
      text = props.actionText;
    }
    if (props.actionText === null) {
      text = null;
      if ((runnerSide && runnerAction) || (riderSide && riderAction)) {
        icon = iconClose;
      }
    }
    const buttonDisabled =
      (runnerSide && riderViewer) ||
      (riderSide && runnerViewer) ||
      props.disabled;
    // output dynamic action button
    return props.micro ? (
      <Box
        className="box-inline action-pointer"
        onClick={
          buttonDisabled
            ? undefined
            : () => handleToggleAction({ runnerSide, riderSide })
        }
      >
        <Typography variant="h1">
          <span className="fa-layers fa-fw">
            <FontAwesomeIcon
              icon={props.icon}
              color={runnerAction || riderAction ? "orange" : "white"}
              className={props.microShake ? "fa-shake" : undefined}
              transform={
                props.microText ? "shrink-6 up-4 rotate-10" : "shrink-6 up-2"
              }
              style={
                props.microShake
                  ? {
                      "--fa-animation-duration": "4s",
                      "--fa-animation-iteration-count": "2",
                    }
                  : {}
              }
            />
            {props.microText && (
              <span
                className="fa-layers-text upper-case bg-black-t90"
                style={{
                  fontSize: 11,
                  fontWeight: "900",
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  border: "1px solid white",
                  marginTop: "16px",
                }}
              >
                {props.microText}
              </span>
            )}
          </span>
        </Typography>
      </Box>
    ) : props.mini ? (
      <ActionButton
        variant="contained"
        size="small"
        color={
          runnerAction || riderAction
            ? "bright"
            : props.color
            ? props.color
            : "dusk"
        }
        fullWidth={props.fullWidth}
        disabled={buttonDisabled}
        disableElevation={props.disableElevation}
        borderGlow={buttonDisabled ? false : props.disableGlow ? false : true}
        border={props.border ? props.border : undefined}
        text={
          <span>
            {text && (
              <span>
                <span>{text}</span>
                <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
              </span>
            )}
            {icon && (
              <span>
                <FontAwesomeIcon icon={icon} transform="grow-4" />
                {/* <FontAwesomeIcon
                  icon={
                    (riderSide && riderAction) || (runnerSide && runnerAction)
                      ? iconClose
                      : iconAdd
                  }
                  transform="shrink-4 right-8"
                /> */}
              </span>
            )}
          </span>
        }
        onClick={
          buttonDisabled
            ? undefined
            : () => handleToggleAction({ runnerSide, riderSide })
        }
      />
    ) : (
      <ActionButton
        variant="contained"
        type="button"
        size="medium"
        color={
          runnerAction || riderAction
            ? "bright"
            : props.color
            ? props.color
            : "primary"
        }
        text={text}
        fullWidth={true}
        disabled={buttonDisabled}
        disableElevation={props.disableElevation}
        borderGlow={buttonDisabled ? false : props.borderGlow ? true : false}
        border={props.border ? props.border : undefined}
        startIcon={
          riderSide && riderAction ? (
            <FontAwesomeIcon
              icon={iconClose}
              style={{
                fontSize: "inherit",
              }}
            />
          ) : null
        }
        endIcon={
          runnerSide && runnerAction ? (
            <FontAwesomeIcon
              icon={iconClose}
              style={{
                fontSize: "inherit",
              }}
            />
          ) : null
        }
        onClick={
          buttonDisabled
            ? undefined
            : () => handleToggleAction({ runnerSide, riderSide })
        }
      />
    );
  };

  return (
    <Box className="box-default">
      <Box className="box-default">
        {renderActionButton({
          runnerSide: runnerViewer,
          riderSide: riderViewer,
        })}
      </Box>
      {/* ---- Action Content ---- */}
      <Drawer
        anchor="bottom"
        open={runnerAction || riderAction}
        onClose={handleCloseAction}
        ModalProps={{ disableScrollLock: props.popMini ? true : false }}
      >
        <Container maxWidth="md">
          <Box
            className={"box-default" + (props.popMini ? "" : " full-height")}
          >
            <Box
              className="box-default text-right"
              sx={{
                pt: configEntzy.APP_SPACING_MD2X,
                pr: configEntzy.APP_SPACING_MD,
              }}
            >
              <Typography variant="h3">
                <FontAwesomeIcon
                  icon={iconClose}
                  className="action-pointer"
                  onClick={handleCloseAction}
                />
              </Typography>
            </Box>
            <Box
              className="box-default"
              // className="box-default shadow-default bg-white-t50"
              sx={{
                // borderRadius: configEntzy.BORDER_SIZE_LG,
                mb: configEntzy.APP_SPACING_LG,
              }}
              hidden={!runnerAction && !riderAction}
            >
              <Box
                className="box-default"
                sx={{
                  pb: configEntzy.APP_SPACING_MD,
                }}
              >
                <Typography variant="h6" color="white">
                  {
                    eventContext.state.event.modules[props.room].text[
                      viewerType
                    ].title
                  }
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{ mt: configEntzy.APP_SPACING_SM }}
                >
                  {
                    eventContext.state.event.modules[props.room].text[
                      viewerType
                    ].description
                  }
                </Typography>
                {props.room === "dates" && (
                  <Typography
                    variant="body2"
                    sx={{ mt: configEntzy.APP_SPACING_MD }}
                  >
                    <span
                      style={{
                        color: "#f90",
                        padding: "4px",
                        borderRadius: "6px",
                        border: "1px solid #f90",
                      }}
                    >
                      <span>&nbsp;&nbsp;</span>
                      <span>Launching&nbsp;at&nbsp;</span>
                      <span>
                        {eventContext.state.event.calendar.triggers.default}
                      </span>
                      <span>&nbsp;Riders&nbsp;with&nbsp;</span>
                      <span>
                        {eventContext.state.event.calendar.limits.notice}
                      </span>
                      <span>&nbsp;Day</span>
                      <span>
                        {eventContext.state.event.calendar.limits.notice === 1
                          ? ""
                          : "s"}
                      </span>
                      <span>&nbsp;Notice</span>
                      <span>&nbsp;&nbsp;</span>
                    </span>
                  </Typography>
                )}
                {props.room === "locations" && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: configEntzy.APP_SPACING_MD,
                      mb: configEntzy.APP_SPACING_MD,
                    }}
                  >
                    <span
                      style={{
                        color: "#f90",
                        padding: "4px",
                        borderRadius: "6px",
                        border: "1px solid #f90",
                      }}
                    >
                      &nbsp;&nbsp; Eventuator Country is{" "}
                      {
                        createGeoCountryItemByCode({
                          shortCode: eventContext.state.event.data.Country,
                        }).name
                      }
                      &nbsp;&nbsp;
                    </span>
                  </Typography>
                )}
                {props.room === "messaging" && (
                  <Fragment>
                    <Box
                      className="box-default"
                      sx={{
                        mt: configEntzy.APP_SPACING_MD,
                      }}
                    >
                      <TextTag
                        size="sm"
                        rounded={true}
                        bgColor="primary.main"
                        color="black"
                        textNoCasing={true}
                        text={
                          <span>
                            <span>&nbsp;&nbsp;</span>
                            <span>Active Chat Room</span>
                            <span>&nbsp;|&nbsp;</span>
                            <span className="proper-case">
                              {eventContext.state.messaging.activeRoom.replace(
                                "|",
                                " | ",
                              )}
                            </span>
                            <span>&nbsp;&nbsp;</span>
                          </span>
                        }
                      />
                    </Box>
                    <Box
                      className="box-default"
                      sx={{
                        mt: configEntzy.APP_SPACING_MD,
                      }}
                    >
                      <Typography variant="body2">
                        <span
                          style={{
                            color: "#f90",
                            padding: "4px",
                            borderRadius: "12px",
                            border: "1px solid #f90",
                          }}
                        >
                          <span>&nbsp;&nbsp;</span>
                          <span>Post Visibility</span>
                          <span>
                            {eventContext.state.messaging.activeRoom.includes(
                              "main",
                            ) && " | Everyone"}
                            {eventContext.state.messaging.activeRoom.includes(
                              "desk",
                            ) && " | Private You & Runners"}
                            {eventContext.state.messaging.activeRoom.includes(
                              "runner",
                            ) && " | Runners"}
                            {eventContext.state.messaging.activeRoom.includes(
                              "rider",
                            ) && " | Riders"}
                            {eventContext.state.messaging.activeRoom.startsWith(
                              "ai",
                            ) && " | AI"}
                          </span>
                          {eventContext.state.messaging.activeRoom.match(
                            /\d{4}-\d{2}-\d{2}$/,
                          ) && (
                            <span>
                              <span>&nbsp;by&nbsp;Date&nbsp;</span>
                              <span>
                                {
                                  eventContext.state.messaging.activeRoom.match(
                                    /\d{4}-\d{2}-\d{2}$/,
                                  )[0]
                                }
                              </span>
                            </span>
                          )}
                          <span>&nbsp;&nbsp;</span>
                        </span>
                      </Typography>
                    </Box>
                  </Fragment>
                )}
              </Box>
              <Divider />
              <Grow
                in={showInfo}
                timeout={{
                  enter: configEntzy.TRANSITIONS.default,
                  exit: configEntzy.TRANSITIONS.default,
                }}
              >
                <Box
                  className="box-default"
                  sx={{
                    p: configEntzy.APP_SPACING_MD,
                    mb: configEntzy.APP_SPACING_MD,
                  }}
                  hidden={!showInfo}
                >
                  <ActionCoreInfo
                    {...props}
                    runnerViewer={runnerViewer}
                    riderViewer={riderViewer}
                    handleCloseAction={handleCloseInfo}
                  />
                </Box>
              </Grow>
              {props.room === "launchpad" && runnerAction && (
                <Box className="box-default">
                  <ActionLaunchpadRunner
                    {...props}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}

              {props.room === "sharing" && runnerAction && (
                <Box className="box-default">
                  <ActionSharingRunner
                    {...props}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "sharing" && riderAction && (
                <Box className="box-default">
                  <ActionSharingRider
                    {...props}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "dates" && runnerAction && (
                <Box className="box-default">
                  <ActionDatesRunner
                    {...props}
                    viewNewOfferOnly={true}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "dates" && riderAction && (
                <Box className="box-default">
                  <ActionDatesRider
                    {...props}
                    viewNewOfferOnly={true}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "messaging" && runnerAction && (
                <Box className="box-default">
                  <ActionMessagingRunner
                    {...props}
                    actionPost={props.subAction === "post"}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "messaging" && riderAction && (
                <Box className="box-default">
                  <ActionMessagingRider
                    {...props}
                    actionPost={props.subAction === "post"}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "locations" && runnerAction && (
                <Box
                  className="box-default"
                  sx={{ mb: configEntzy.APP_SPACING_XL }}
                >
                  <ActionLocationsRunner
                    {...props}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "locations" && riderAction && (
                <Box
                  className="box-default"
                  sx={{ mb: configEntzy.APP_SPACING_XL }}
                >
                  <ActionLocationsRider
                    {...props}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "entry" && runnerAction && (
                <Box className="box-default">
                  <ActionEntryRunner
                    {...props}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "entry" && riderAction && (
                <Box className="box-default">
                  <ActionEntryRider
                    {...props}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "members" && runnerAction && (
                <Box className="box-default">
                  <ActionMembersRunner
                    {...props}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
              {props.room === "members" && riderAction && (
                <Box className="box-default">
                  <ActionMembersRider
                    {...props}
                    handleCloseAction={handleCloseAction}
                    handleOpenAction={handleOpenAction}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Drawer>
    </Box>
  );
}

export default ActionCore;
